import React, { useState, useEffect } from 'react';
import styles from './Matrices.module.css';
import { MATRIX_TYPES, COMMON_MATRIX_CODES, validateMatrix } from '../../../../types/matrix.types.js';

const MatrixForm = ({ matrix, onSubmit, onCancel, loading }) => {
    const [formData, setFormData] = useState({
        matrix: '',
        description: '',
        matrixType: 'Liquid',
        active: true
    });
    
    const [errors, setErrors] = useState({});
    const [showCommonCodes, setShowCommonCodes] = useState(false);

    const isEditMode = !!matrix;

    // Initialize form with existing data when editing
    useEffect(() => {
        if (matrix) {
            setFormData({
                matrix: matrix.matrix || '',
                description: matrix.description || '',
                matrixType: matrix.matrixType || 'Liquid',
                active: matrix.active !== undefined ? matrix.active : true
            });
        }
    }, [matrix]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.matrix || formData.matrix.trim().length === 0) {
            newErrors.matrix = 'Matrix name is required';
        }

        if (formData.matrix && formData.matrix.length > 100) {
            newErrors.matrix = 'Matrix name cannot exceed 100 characters';
        }

        if (formData.description && formData.description.length > 254) {
            newErrors.description = 'Description cannot exceed 254 characters';
        }

        if (formData.matrixType && formData.matrixType.length > 50) {
            newErrors.matrixType = 'Matrix type cannot exceed 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        
        // Clear error for this field
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleUseCommonCode = (commonCode) => {
        setFormData({
            matrix: commonCode.name,
            description: commonCode.description,
            matrixType: commonCode.type,
            active: true
        });
        setShowCommonCodes(false);
        setErrors({});
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formHeader}>
                <h3>{matrix ? 'Edit Matrix' : 'Add New Matrix'}</h3>
                {!isEditMode && (
                    <button
                        type="button"
                        className={styles.commonCodesButton}
                        onClick={() => setShowCommonCodes(!showCommonCodes)}
                    >
                        {showCommonCodes ? 'Hide' : 'Show'} Common Codes
                    </button>
                )}
            </div>

            {showCommonCodes && !isEditMode && (
                <div className={styles.commonCodesPanel}>
                    <h4>Common Matrix Codes</h4>
                    <p className={styles.commonCodesHint}>Click any code to use it as a template</p>
                    <div className={styles.commonCodesList}>
                        {COMMON_MATRIX_CODES.map((code) => (
                            <button
                                key={code.code}
                                type="button"
                                className={styles.commonCodeItem}
                                onClick={() => handleUseCommonCode(code)}
                            >
                                <strong>{code.code}</strong>
                                <span>{code.name}</span>
                                <small>{code.type}</small>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className={styles.formBody}>
                <div className={styles.field}>
                    <label className={styles.label}>Matrix Name *</label>
                    <input
                        type="text"
                        name="matrix"
                        value={formData.matrix}
                        onChange={handleChange}
                        className={`${styles.input} ${errors.matrix ? styles.inputError : ''}`}
                        placeholder="Enter matrix name (e.g., Aqueous, Soil, Ground Water)"
                        maxLength={100}
                        disabled={loading}
                    />
                    {errors.matrix && (
                        <span className={styles.errorText}>{errors.matrix}</span>
                    )}
                    <small className={styles.hint}>
                        {formData.matrix.length}/100 characters
                    </small>
                </div>

                <div className={styles.field}>
                    <label className={styles.label}>Matrix Type</label>
                    <select
                        name="matrixType"
                        value={formData.matrixType}
                        onChange={handleChange}
                        className={styles.input}
                        disabled={loading}
                    >
                        <option value="">Select type...</option>
                        <option value={MATRIX_TYPES.LIQUID}>{MATRIX_TYPES.LIQUID}</option>
                        <option value={MATRIX_TYPES.SOLID}>{MATRIX_TYPES.SOLID}</option>
                        <option value={MATRIX_TYPES.GAS}>{MATRIX_TYPES.GAS}</option>
                    </select>
                    <small className={styles.hint}>
                        Physical form of the sample
                    </small>
                </div>

                <div className={styles.field}>
                    <label className={styles.label}>Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className={`${styles.textarea} ${errors.description ? styles.inputError : ''}`}
                        placeholder="Enter description (optional)"
                        maxLength={254}
                        rows={4}
                        disabled={loading}
                    />
                    {errors.description && (
                        <span className={styles.errorText}>{errors.description}</span>
                    )}
                    <small className={styles.hint}>
                        {formData.description.length}/254 characters
                    </small>
                </div>

                <div className={styles.field}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleChange}
                            disabled={loading}
                        />
                        <span>Active</span>
                    </label>
                    <small className={styles.hint}>
                        Inactive matrices won't appear in dropdowns
                    </small>
                </div>
            </div>

            <div className={styles.formFooter}>
                <button 
                    type="button"
                    onClick={onCancel}
                    className={styles.cancelButton}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button 
                    type="submit"
                    className={styles.submitButton}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (matrix ? 'Update' : 'Create')}
                </button>
            </div>
        </form>
    );
};

export default MatrixForm;
